import React, { useEffect, useState } from 'react';
import { Feedback, useSectionFeedback } from './sectionFeedback';

const FONTS = ['Arial', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Poppins', 'Jost'];

const PRODUCT_TILE_SIZES = [
    { id: 'compact', label: 'Compact' },
    { id: 'standard', label: 'Standard' },
    { id: 'large', label: 'Large' },
];

const HOMEPAGE_TEMPLATES = [
    {
        id: 'productsFirst',
        label: 'Products First',
        description: 'Your products appear before your links section.',
    },
    {
        id: 'linksFirst',
        label: 'Links First',
        description: 'Your links section appears before your products.',
    },
    {
        id: 'productsOnly',
        label: 'Products Only',
        description: 'Hide the links section on your homepage entirely.',
    },
];

const PRODUCT_DETAIL_TEMPLATES = [
    {
        id: 'standard',
        label: 'Standard',
        description: 'Product image and description, then the media gallery.',
    },
    {
        id: 'galleryFirst',
        label: 'Gallery First',
        description: 'Promote the media gallery above the product description.',
    },
    {
        id: 'compact',
        label: 'Compact',
        description: 'Tighter spacing, with the extended description collapsed by default.',
    },
];

const DEFAULTS = {
    font: 'Jost',
    numHomepageProducts: 4,
    productTileSize: 'standard',
    homepageTemplate: 'productsFirst',
    productDetailTemplate: 'standard',
};

function optionsOrFallback(options, fallback) {
    return Array.isArray(options) && options.length > 0 ? options : fallback;
}

export default function CustomizationSection({ data, saving, onSave, actions }) {
    const branding = data?.branding || {};
    const storefrontLayout = data?.storefrontLayout || {};
    const layoutOptions = data?.storefrontLayoutOptions || {};
    const productTileSizes = optionsOrFallback(layoutOptions.productTileSizes, PRODUCT_TILE_SIZES);
    const homepageTemplates = optionsOrFallback(layoutOptions.homepageTemplates, HOMEPAGE_TEMPLATES);
    const productDetailTemplates = optionsOrFallback(
        layoutOptions.productDetailTemplates,
        PRODUCT_DETAIL_TEMPLATES
    );
    const [form, setForm] = useState({});
    const { feedback, showFeedback } = useSectionFeedback();

    useEffect(() => {
        setForm({
            font: branding.font || DEFAULTS.font,
            blurBackgroundImage: typeof branding.blurBackgroundImage === 'undefined' ? true : !!branding.blurBackgroundImage,
            numHomepageProducts: branding.numHomepageProducts || DEFAULTS.numHomepageProducts,
            productTileSize: storefrontLayout.productTileSize
                || branding.productTileSize
                || DEFAULTS.productTileSize,
            homepageTemplate: storefrontLayout.homepageTemplate
                || branding.homepageTemplate
                || DEFAULTS.homepageTemplate,
            productDetailTemplate: storefrontLayout.productDetailTemplate
                || branding.productDetailTemplate
                || DEFAULTS.productDetailTemplate,
        });
        // `branding`/`storefrontLayout` fall back to freshly-allocated `{}` literals when
        // absent from `data`, so depending on them directly would re-run this effect (and
        // re-render) forever. Depend on the primitive fields instead.
    }, [
        branding.font,
        branding.blurBackgroundImage,
        branding.numHomepageProducts,
        branding.productTileSize,
        branding.homepageTemplate,
        branding.productDetailTemplate,
        storefrontLayout.productTileSize,
        storefrontLayout.homepageTemplate,
        storefrontLayout.productDetailTemplate,
    ]);

    function updateField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function handleSave() {
        onSave(async () => {
            const brandingResult = await actions.saveBranding({
                ...form,
                numHomepageProducts: parseInt(form.numHomepageProducts, 10),
            });
            showFeedback(
                brandingResult.success ? 'success' : 'error',
                brandingResult.success ? 'Customization saved.' : brandingResult.error
            );
            return brandingResult;
        });
    }

    return (
        <div>
            <div className="sfm-section-header">
                <h2>Customization</h2>
                <p>
                    Choose storefront typography, background image blur, and structural layout
                    templates for your homepage, product tiles, and product detail pages.
                </p>
            </div>
            <div className="group">
                <label htmlFor="sfm-font">Font</label>
                <select id="sfm-font" value={form.font || DEFAULTS.font} onChange={event => updateField('font', event.target.value)}>
                    {FONTS.map(font => <option key={font} value={font}>{font}</option>)}
                </select>
            </div>
            <div className="group">
                <label className="checkbox" htmlFor="sfm-blur-background">
                    <input id="sfm-blur-background" type="checkbox" checked={!!form.blurBackgroundImage} onChange={event => updateField('blurBackgroundImage', event.target.checked)} aria-describedby="sfm-blur-background-help" />
                    <span>Blur Background Image</span>
                </label>
                <p id="sfm-blur-background-help" className="help">Applies a soft blur to the background image so text stays readable.</p>
            </div>
            <div className="group">
                <label htmlFor="sfm-homepage-products">Products on Homepage</label>
                <input
                    id="sfm-homepage-products"
                    type="number"
                    min="1"
                    max="20"
                    value={form.numHomepageProducts ?? ''}
                    onChange={event => updateField('numHomepageProducts', event.target.value)}
                    aria-describedby="sfm-homepage-products-help"
                />
                <p id="sfm-homepage-products-help" className="help">How many of your products appear on the homepage. Between 1 and 20.</p>
            </div>
            <fieldset className="sfm-segmented-field">
                <legend>Product Tile Size</legend>
                <p className="help">Applies to your homepage, folder listings, and full store listing.</p>
                <div className="sfm-segmented" role="group" aria-label="Product tile size">
                    {productTileSizes.map(option => (
                        <button
                            key={option.id}
                            type="button"
                            className={`sfm-segmented-option${form.productTileSize === option.id ? ' selected' : ''}`}
                            aria-pressed={form.productTileSize === option.id}
                            onClick={() => updateField('productTileSize', option.id)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </fieldset>
            <fieldset className="sfm-template-presets">
                <legend>Homepage Layout</legend>
                <p className="help">Choose how your products and links sections are arranged on your homepage.</p>
                <div className="sfm-template-preset-grid">
                    {homepageTemplates.map(template => {
                        const selected = form.homepageTemplate === template.id;
                        return (
                            <button
                                key={template.id}
                                type="button"
                                className={`sfm-template-preset${selected ? ' selected' : ''}`}
                                aria-pressed={selected}
                                onClick={() => updateField('homepageTemplate', template.id)}
                            >
                                <span className="sfm-template-preset-label">{template.label}</span>
                                <span className="sfm-template-preset-description">{template.description}</span>
                            </button>
                        );
                    })}
                </div>
            </fieldset>
            <fieldset className="sfm-template-presets">
                <legend>Product Detail Layout</legend>
                <p className="help">Choose how the product image, description, and gallery are arranged on your product detail pages.</p>
                <div className="sfm-template-preset-grid">
                    {productDetailTemplates.map(template => {
                        const selected = form.productDetailTemplate === template.id;
                        return (
                            <button
                                key={template.id}
                                type="button"
                                className={`sfm-template-preset${selected ? ' selected' : ''}`}
                                aria-pressed={selected}
                                onClick={() => updateField('productDetailTemplate', template.id)}
                            >
                                <span className="sfm-template-preset-label">{template.label}</span>
                                <span className="sfm-template-preset-description">{template.description}</span>
                            </button>
                        );
                    })}
                </div>
            </fieldset>
            <div className="sfm-save-row">
                <button type="button" className="button primary" onClick={handleSave} disabled={saving}>Save Customization</button>
                <Feedback feedback={feedback} />
            </div>
        </div>
    );
}
